import { StatusModal } from "@components";
import { AlertOctagon } from "@untitledui/icons";
import dayjs from "dayjs";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";
import { useCreateVehicleOdometerChecklist } from "@/features/vehicle/query";
import type { VehicleOdometerLatestChecklistResponse } from "@/features/vehicle/types/odometer-ui.types";

type OdometerChecklistUpdateModalProps = {
	vehicleId: string;
	latestChecklistData:
		| VehicleOdometerLatestChecklistResponse
		| null
		| undefined;
	isLoadingLatestChecklist: boolean;
	onClose: () => void;
	onRequestManual: () => void;
};

export function OdometerChecklistUpdateModal({
	vehicleId,
	latestChecklistData,
	isLoadingLatestChecklist,
	onClose,
	onRequestManual,
}: OdometerChecklistUpdateModalProps) {
	const [step, setStep] = useState<"default" | "success">("default");
	const { mutateAsync: createChecklistUpdate, isPending: isApplyingChecklist } =
		useCreateVehicleOdometerChecklist();

	const handleClose = () => {
		setStep("default");
		onClose();
	};

	const openManualFromChecklist = () => {
		setStep("default");
		onClose();
		onRequestManual();
	};

	const handleChecklistApply = async () => {
		if (!latestChecklistData) {
			return;
		}
		try {
			await createChecklistUpdate({
				vehicleId,
				payload: {
					checklistAnswerId: latestChecklistData.checklistAnswerId,
					newOdometer: latestChecklistData.checklistOdometer,
					readingDateTime: dayjs(
						latestChecklistData.checklistSubmittedAt
					).toISOString(),
				},
			});
			setStep("success");
		} catch (_error) {
			toast.error("Não foi possível atualizar via checklist.");
		}
	};

	const hasChecklistData = !!latestChecklistData;
	const hasSuccess = step === "success";
	const hasDefaultWithData = step === "default" && hasChecklistData;
	const isNotFound = step === "default" && !hasChecklistData;

	let actions: Array<{
		label: string;
		onClick: () => void;
		color?: "primary" | "secondary";
		isLoading?: boolean;
		showTextWhileLoading?: boolean;
	}> = [];
	let description = "";
	let title = "";
	let icon: ReactNode = null;

	if (hasSuccess) {
		actions = [
			{
				label: "Fechar",
				onClick: handleClose,
				color: "secondary",
			},
		];
		description = "Clique em fechar para ver o novo valor.";
		title = "Atualização concluída!";
	} else if (hasDefaultWithData) {
		actions = [
			{
				label: "Usar checklist",
				onClick: handleChecklistApply,
				isLoading: isApplyingChecklist,
				showTextWhileLoading: true,
			},
			{ label: "Fazer Manualmente", onClick: openManualFromChecklist },
		];
		description = "Clique em fechar para ver o novo valor.";
		title = "Últimos Dados do Checklist";
	} else {
		actions = [
			{ label: "Fazer Manualmente", onClick: openManualFromChecklist },
		];
		description = isLoadingLatestChecklist
			? "Buscando último checklist..."
			: "Para atualizar o Hodômetro, clique em Fazer Manualmente";
		title = "Não encontramos checklist";
		icon = (
			<AlertOctagon className="absolute top-6 left-6 size-12 rounded-full bg-bg-brand-primary p-3 text-primary" />
		);
	}

	const content = hasDefaultWithData ? (
		<div className="grid w-full grid-cols-2 overflow-hidden rounded-[4px] border border-[#d5d7da]">
			<div className="bg-[#f1f4f6] p-4">
				<p className="mb-2 font-bold text-fg-primary text-sm">
					Último Registro
				</p>
				<p className="font-bold text-fg-primary text-xl">
					{latestChecklistData.checklistOdometer.toLocaleString("pt-BR")}{" "}
					<span className="font-medium text-sm">km</span>
				</p>
			</div>
			<div className="bg-[#f1f4f6] p-4">
				<p className="mb-2 font-bold text-fg-primary text-sm">Data e Hora</p>
				<p className="font-medium text-fg-primary text-xs">
					{latestChecklistData.checklistSubmittedAt
						? dayjs(latestChecklistData.checklistSubmittedAt).format(
								"DD/MM/YYYY [às] HH:mm"
							)
						: "-"}
				</p>
			</div>
		</div>
	) : null;

	return (
		<StatusModal
			actions={actions}
			content={content}
			description={description}
			icon={icon}
			isOpen
			onClose={handleClose}
			title={title}
			variant={isNotFound ? "info" : "success"}
		/>
	);
}
